import { FC, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Box, Checkbox, FormInputText, Svg, SvgBubble, Text, Tooltip } from '@hopskipdrive/react-components';
import { colorTokens } from '@rideiq/shared/components/variables';
import { differenceInYears } from 'date-fns';
import * as Yup from 'yup';

import { useRiderValidation } from '../../../../hooks/useRiderValidation';
import { useToastStore } from '../../../../shared/stores/toastStore';
import { RiderServices } from '../../../components/RiderServices';

import { Divider, InputLabel, MarginLeft } from './SafetySettingsForm.styles';
import { ISafetySettings } from './types';

interface SafetySettingsFormProps {
  onSubmit: (data) => void;
  onCancel: () => void;
  safetySettings: ISafetySettings;
  dateOfBirth: Nullable<Date>;
  setIsSubmitting: (isSubmitting: boolean) => void;
  isInitialized: boolean;
  setIsInitialized: (isInitialized: boolean) => void;
}

export const SafetySettingsForm: FC<SafetySettingsFormProps> = ({
  isInitialized,
  setIsInitialized,
  safetySettings,
  dateOfBirth,
  onSubmit,
  onCancel,
  setIsSubmitting,
}) => {
  const { validateRiderDetails, isFetching } = useRiderValidation();
  const { addToast } = useToastStore();

  const methods = useForm<ISafetySettings>({
    defaultValues: useMemo(() => {
      return {
        codeWord: safetySettings.codeWord,
        riderNotes: safetySettings.riderNotes,
        boosterSeat: safetySettings.boosterSeat,
        frontSeatAuthorized: safetySettings.frontSeatAuthorized,
        mustBeMet: safetySettings.mustBeMet,
        services: safetySettings.services,
      };
    }, [safetySettings]),
    resolver: yupResolver(validationSchema),
  });

  const {
    control,
    handleSubmit,
    setValue,
    setError,
    formState: { errors, isDirty },
  } = methods;

  let age;

  if (dateOfBirth) {
    age = differenceInYears(Date.now(), new Date(dateOfBirth));
  }
  const handleValidation = async (data) => {
    const abortController = new AbortController();

    setIsSubmitting(true);
    const response = await validateRiderDetails({ codeWord: data.codeWord });

    if (abortController.signal.aborted) {
      return;
    }
    if (response.type === 'error') {
      const errorMessages = response.data?.errors;

      for (const property in errorMessages) {
        switch (property) {
          case 'codeWord':
            setError('codeWord', { message: errorMessages.codeWord });
            break;
          default:
            addToast({
              severity: 'error',
              message:
                typeof errorMessages[property] === 'string'
                  ? errorMessages[property]
                  : `Incorrect input for ${property}`,
            });
            break;
        }
      }
    } else {
      onSubmit(data);
    }
    setIsSubmitting(false);
  };

  return (
    <form
      id="safety-settings"
      aria-label="form"
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleSubmit((data: ISafetySettings) => (isDirty ? !isFetching && handleValidation(data) : onCancel()))();
      }}
    >
      <CheckboxGroup control={control} age={age} setValue={setValue} />
      <Alert
        sx={{ m: '10px 0' }}
        severity="info"
        message={
          <span>
            The <b>Must Be Met</b> requirement is now location-specific! You’ll be able to enable <b>Must Be Met</b> in
            later steps, or navigate to this rider’s assigned locations to enable or disable <b>Must Be Met</b>.
          </span>
        }
      />

      <CodeWord control={control} errors={errors} />
      <Divider />
      <RiderNotes control={control} errors={errors} />
      <FormProvider {...methods}>
        <RiderServices
          editFlow={false}
          isInitialized={isInitialized}
          setIsInitialized={setIsInitialized}
          dateOfBirth={dateOfBirth}
        />
      </FormProvider>
    </form>
  );
};

const CheckboxGroup = ({ control, age, setValue }) => (
  <Box display="flex" alignItems="center" sx={{ flexWrap: { xs: 'wrap', sm: 'nowrap' }, rowGap: { xs: 1, sm: 0 } }}>
    <Controller
      control={control}
      name="frontSeatAuthorized"
      render={({ field: { onChange, onBlur, value } }) => {
        return (
          <Box alignItems="center" mr={6}>
            <Checkbox
              id="frontSeatAuthorized"
              data-testid="frontSeatAuthorized"
              onBlur={onBlur}
              onChange={(e) => {
                onChange(e.target.checked);
                if (e.target.checked) {
                  setValue('boosterSeat', false);
                }
              }}
              checked={value}
              disabled={cannotSitUpFront(age)}
            />
            <InputLabel htmlFor="frontSeatAuthorized">Can sit up front</InputLabel>
          </Box>
        );
      }}
    />
    <Controller
      control={control}
      name="boosterSeat"
      render={({ field: { onChange, onBlur, value } }) => (
        <Box alignItems="center">
          <Checkbox
            id="boosterSeat"
            data-testid="boosterSeat"
            onBlur={onBlur}
            onChange={(e) => {
              onChange(e.target.checked);
              if (e.target.checked) {
                setValue('frontSeatAuthorized', false);
              }
            }}
            checked={value}
          />
          <InputLabel htmlFor="boosterSeat">Needs a booster seat</InputLabel>
        </Box>
      )}
    />
  </Box>
);

const CodeWord = ({ errors, control }) => (
  <Box
    display="flex"
    sx={{
      flexDirection: { xs: 'column', sm: 'row' },
      alignItems: { xs: 'flex-start', sm: 'center' },
      gap: { xs: 1, sm: 3 },
      width: '100%',
    }}
  >
    <Box display="flex" alignItems="center" sx={{ flexShrink: 0 }}>
      <InputLabel fieldtype="required" htmlFor="codeWord" style={{ width: 'auto' }}>
        Code word
      </InputLabel>
      <MarginLeft>
        <Tooltip title="Create a secret code word that your rider can remember. The CareDriver will say this at pick-up so your rider knows they are with the right person.">
          <SvgBubble>
            <Svg icon="question" />
          </SvgBubble>
        </Tooltip>
      </MarginLeft>
    </Box>
    <Controller
      control={control}
      name="codeWord"
      render={({ field: { onChange, value } }) => (
        <FormInputText
          inputProps={{ 'aria-required': true }}
          id="codeWord"
          value={value}
          name="codeWord"
          variant="outlined"
          data-testid="code-word"
          error={!!errors.codeWord}
          onChange={onChange}
          helperText={errors.codeWord ? errors.codeWord.message : ''}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        />
      )}
    />
  </Box>
);

const RiderNotes = ({ errors, control }) => {
  return (
    <Box
      display="flex"
      alignItems="center"
      mt={6}
      sx={{
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: 'flex-start',
        gap: { xs: 2, sm: 3 },
        width: '100%',
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <InputLabel htmlFor="riderNotes" style={{ width: 'auto' }}>
          Rider notes
        </InputLabel>
      </Box>
      <Controller
        control={control}
        name="riderNotes"
        render={({ field: { onChange, value } }) => (
          <FormInputText
            sx={{ width: { xs: '100%', sm: '352px' } }}
            id="riderNotes"
            value={value}
            name="riderNotes"
            placeholder="Ex: Non-speaking, sensitive to loud noise. Use child lock on door and do not play any music."
            multiline
            rows={4}
            variant="outlined"
            data-testid="rider-notes"
            error={!!errors.riderNotes}
            onChange={onChange}
            helperText={(() => {
              if (errors.riderNotes) {
                return errors.riderNotes.message;
              }
              if (value.length === 0) {
                return <span style={{ color: '#6B7280' }}>Max. 500 characters</span>;
              }

              return (
                <span>
                  {value.length >= 500
                    ? `Max. ${Math.min(value.length, 500)} characters`
                    : `${Math.min(value.length, 500)}/500 characters`}
                </span>
              );
            })()}
          />
        )}
      />
      <Text
        variant="caption"
        textColor="secondaryText"
        ml={2}
        mb={4}
        sx={{
          background: colorTokens.background.light,
          width: { xs: '100%', sm: '352px' },
          padding: '12px',
          textAlign: 'left',
        }}
      >
        Rider notes should highlight crucial rider-specific details and actions required to enable safety and support
        for the rider. Notes should be concise, complete, and actionable.
      </Text>
    </Box>
  );
};

const validationSchema = Yup.object().shape({
  codeWord: Yup.string().required('Required Field').max(34, 'Code word exceeds 34 character max'),
  riderNotes: Yup.string().max(500, 'Max. 500 characters'),
});

const cannotSitUpFront = (age) => {
  if (!isNaN(age)) {
    if (age <= 11) {
      return true;
    }
  }

  return false;
};
